/**
 * settings/time-zone-api — strefa czasowa tenanta, Ustawienia → „Ogólne”.
 *
 * Strefa tenanta to strefa, w której liczy logika: data dokumentu „dziś”, numeracja (rok/miesiąc),
 * terminy w mailach, harmonogram KSeF. Godziny na ekranach każdy widzi na zegarze swojej
 * przeglądarki (`lib/user-date-time.ts`) — tej strefy tu nie ma.
 *
 * Endpointy (`/api/settings/time-zone`):
 *   GET  — `{ timeZone, canManage }`; dostępny dla każdego zalogowanego użytkownika tenanta,
 *          `canManage` mówi, czy osoba może strefę zmienić (super-user albo
 *          `crm_core.time_zone.manage`) — panel nie liczy tego sam,
 *   PUT  — `{ timeZone }` → `{ timeZone }`; błędy: 403 `PERMISSION_DENIED`,
 *          400 `INVALID_TIME_ZONE` (nieznana strefa) / `VALIDATION_ERROR` / `INVALID_BODY`.
 *
 * `useTenantTimeZone()` daje samą strefę miejscom, które liczą w strefie tenanta (podgląd
 * numeracji, data przyjęcia na PDF faktury KSeF). Dopóki odpowiedź nie przyszła albo przy błędzie —
 * `DEFAULT_TIME_ZONE`.
 */

import {
  DEFAULT_TIME_ZONE,
  isValidTimeZone,
  type TenantTimeZoneSettingResponse,
  type TenantTimeZoneSettingUpdate,
  type TenantTimeZoneSettingUpdateResponse,
} from '@saas/shared';
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { api } from '@/lib/api';

export const TENANT_TIME_ZONE_KEY = ['settings', 'time-zone'] as const;

/** Ustawienie zmienia się rzadko — 5 minut bez ponownego pytania serwera wystarcza. */
const TENANT_TIME_ZONE_STALE_MS = 5 * 60 * 1000;

/** Kod błędu 400 — serwer nie zna podanej strefy. */
export const INVALID_TIME_ZONE_CODE = 'INVALID_TIME_ZONE';

/** Czy błąd zapisu to nieznana strefa (400 `INVALID_TIME_ZONE`). */
export function isInvalidTimeZoneError(err: unknown): boolean {
  return (
    isAxiosError<{ error?: string }>(err) &&
    err.response?.status === 400 &&
    err.response.data?.error === INVALID_TIME_ZONE_CODE
  );
}

/** Czy błąd zapisu to brak uprawnienia (403). */
export function isPermissionDeniedError(err: unknown): boolean {
  return isAxiosError(err) && err.response?.status === 403;
}

export async function fetchTenantTimeZone(): Promise<TenantTimeZoneSettingResponse> {
  const res = await api.get<TenantTimeZoneSettingResponse>('/settings/time-zone');
  return res.data;
}

export async function updateTenantTimeZone(timeZone: string): Promise<string> {
  const body: TenantTimeZoneSettingUpdate = { timeZone };
  const res = await api.put<TenantTimeZoneSettingUpdateResponse>('/settings/time-zone', body);
  return res.data.timeZone;
}

/** Pełne ustawienie (strefa + `canManage`) — dla zakładki „Ogólne”. */
export function useTenantTimeZoneSetting(): UseQueryResult<TenantTimeZoneSettingResponse, Error> {
  return useQuery<TenantTimeZoneSettingResponse, Error>({
    queryKey: TENANT_TIME_ZONE_KEY,
    queryFn: fetchTenantTimeZone,
    staleTime: TENANT_TIME_ZONE_STALE_MS,
    retry: false,
  });
}

/**
 * Strefa tenanta do liczenia w panelu. Do czasu odpowiedzi serwera, przy błędzie albo przy
 * strefie nieznanej przeglądarce — `DEFAULT_TIME_ZONE`.
 */
export function useTenantTimeZone(): string {
  const { data } = useTenantTimeZoneSetting();
  const timeZone = data?.timeZone;
  return isValidTimeZone(timeZone) ? timeZone : DEFAULT_TIME_ZONE;
}

/** Zapis strefy; po nim (także nieudanym) ustawienie jest pobierane ponownie z serwera. */
export function useUpdateTenantTimeZone(): UseMutationResult<string, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<string, Error, string>({
    mutationFn: updateTenantTimeZone,
    onSettled: () => queryClient.invalidateQueries({ queryKey: TENANT_TIME_ZONE_KEY }),
  });
}
